// Three-pane inbox layout shell (docs/06-ux-rules.md "Layout" / P10 D1-D5;
// 11-chassis-plan.md PR 02). Slots are compound components passed as
// `children` — `InboxLayout.FilterBar` / `.List` / `.Thread` / `.Rail` —
// never JSX-as-props (repo convention). `FeedbackInbox` composes them:
//
//   <InboxLayout>
//     <InboxLayout.FilterBar>...</InboxLayout.FilterBar>
//     <InboxLayout.List>...</InboxLayout.List>
//     <InboxLayout.Thread>...</InboxLayout.Thread>
//     <InboxLayout.Rail>...</InboxLayout.Rail>
//   </InboxLayout>
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMediaQuery } from 'react-responsive'
import { useStyletron } from 'styletron-react'
import { useIsDesktop } from 'utilities/responsive'
import {
  INBOX_BORDER_WIDTH_PX,
  INBOX_CARD_BORDER_RADIUS_PX,
  INBOX_CARD_GAP_PX,
  INBOX_PAGE_PADDING_PX,
  INBOX_PANES_MIN_HEIGHT_PX,
  LIST_PANE_WIDTH_PX,
  NARROW_VIEWPORT_MAX_WIDTH_PX,
  RAIL_WIDTH_PX,
} from './constants'

const t = buildTranslate('feedback_inbox.layout')

// Narrow-viewport rail drawer. The prototype has no drawer state (v83 ships
// no responsive rules), so this value is our own. Literal rgba, same
// precedent as CaseActionsMenu.tsx / RecipientPicker.tsx.
const RAIL_DRAWER_SHADOW = '-2px 0 12px rgba(0, 0, 0, 0.15)'

// Prototype's exact `.inbox3` / `.f-topbar` card shadow (v83) — a literal
// rgba, same precedent as RAIL_DRAWER_SHADOW above. Shared by both the
// filter bar card and the panes card below it.
const INBOX_CARD_SHADOW = '0 1px 3px rgba(30, 38, 48, 0.05)'

interface SlotProps {
  children: React.ReactNode
}

// Slot marker components — InboxLayout reads their children back out by
// matching on `child.type`, it never renders these directly.
const FilterBarSlot = ({ children }: SlotProps) => <>{children}</>
const ListSlot = ({ children }: SlotProps) => <>{children}</>
const ThreadSlot = ({ children }: SlotProps) => <>{children}</>
const RailSlot = ({ children }: SlotProps) => <>{children}</>

function findSlot(
  children: React.ReactNode,
  slot: React.ComponentType<SlotProps>
): React.ReactNode {
  const match = React.Children.toArray(children).find(
    (child): child is React.ReactElement<SlotProps> =>
      React.isValidElement(child) && child.type === slot
  )
  return match ? match.props.children : null
}

export interface InboxRailControls {
  railOpen: boolean
  openRail: () => void
  closeRail: () => void
}

const InboxRailContext = createContext<InboxRailControls | null>(null)

// Exposed so the Thread slot's header (PR 05's "Details" toggle) can open/
// close the rail drawer without InboxLayout having to prop-drill through
// every pane.
export function useInboxRailControls(): InboxRailControls {
  const context = useContext(InboxRailContext)
  if (!context) {
    throw new Error('useInboxRailControls must be used within InboxLayout')
  }
  return context
}

interface Props {
  children: React.ReactNode
}

const InboxLayout = ({ children }: Props) => {
  const [css] = useStyletron()
  const isDesktop = useIsDesktop()
  const isNarrow = useMediaQuery({ maxWidth: NARROW_VIEWPORT_MAX_WIDTH_PX })
  const [railOpen, setRailOpen] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement | null>(null)

  const openRail = useCallback(() => {
    triggerRef.current = document.activeElement as HTMLElement | null
    setRailOpen(true)
  }, [])

  const closeRail = useCallback(() => {
    setRailOpen(false)
  }, [])

  // Resizing back to desktop shouldn't leave a dangling open drawer.
  useEffect(() => {
    if (isDesktop) setRailOpen(false)
  }, [isDesktop])

  // Focus moves into the drawer on open, and returns to whatever triggered
  // it on close (docs/06-ux-rules.md "Responsive floor").
  useEffect(() => {
    if (railOpen) {
      drawerRef.current?.focus()
    } else {
      triggerRef.current?.focus()
      triggerRef.current = null
    }
  }, [railOpen])

  useEffect(() => {
    if (!railOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRail()
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        closeRail()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [railOpen, closeRail])

  const railControls = useMemo(
    () => ({ railOpen, openRail, closeRail }),
    [railOpen, openRail, closeRail]
  )

  if (isNarrow) {
    return (
      <div className={css({ padding: '24px' })}>
        {t('narrow_viewport_notice')}
      </div>
    )
  }

  // Explicit `height: 100%` (rather than relying solely on the grid row's
  // implicit stretch) so a pane's own children can safely size themselves
  // off a percentage of THIS pane (e.g. the composer's max-height safety
  // valve, docs/06-ux-rules.md "Composer" / "Panel") against a definite
  // ancestor instead of an ambiguous ("auto") ancestor height.
  const paneStyle = css({ height: '100%', minHeight: 0, overflow: 'auto' })
  // Vertical dividers so the list/thread/rail panes read as one card rather
  // than three flush blocks (prototype/v69 `.ib-list` / `.ib-details`,
  // index.html:436, 486). The thread (middle) pane has no divider of its
  // own — it's bounded by its neighbors' borders on both sides.
  const listPaneStyle = css({
    borderRight: `${INBOX_BORDER_WIDTH_PX}px solid ${palette.grey30}`,
  })
  const railPaneStyle = css({
    borderLeft: `${INBOX_BORDER_WIDTH_PX}px solid ${palette.grey30}`,
  })
  // Fill the card so the bar can spread its zones edge to edge.
  const filterBarSectionStyle = css({ flex: 1, minWidth: 0 })

  return (
    <InboxRailContext.Provider value={railControls}>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: `${INBOX_CARD_GAP_PX}px`,
          backgroundColor: palette.grey05,
          padding: `${INBOX_PAGE_PADDING_PX}px`,
          boxSizing: 'border-box',
          flex: '1',
          // Without this, the default `min-height: auto` on a flex item uses
          // its content's intrinsic height as a floor, so `flex: 1` can't
          // shrink this below the panes-grid's full content height (e.g. a
          // long feedback list) — this whole card grows past its
          // HostFlexShell-bound parent instead of clipping, and the page
          // scrolls instead of the list pane. Confirmed live: with 40+ open
          // items, this div's rendered height matched its content (~3490px)
          // regardless of the ~560px its flex parent actually had to offer.
          minHeight: 0,
        })}
      >
        <div
          className={css({
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '11px 14px',
            backgroundColor: palette.white,
            border: `${INBOX_BORDER_WIDTH_PX}px solid ${palette.grey20}`,
            borderRadius: `${INBOX_CARD_BORDER_RADIUS_PX}px`,
            boxShadow: INBOX_CARD_SHADOW,
          })}
        >
          <section
            aria-label={t('filter_bar_landmark')}
            className={filterBarSectionStyle}
          >
            {findSlot(children, FilterBarSlot)}
          </section>
        </div>
        {/* The floor (docs/06-ux-rules.md "Height model" / P10 D2): below
          INBOX_PANES_MIN_HEIGHT_PX the page scrolls rather than the card
          getting shorter. Height otherwise comes entirely from `flex: 1`
          against the root's now-definite height (set by HostFlexShell up
          the tree) — no `height: 100%` here, since the root's content box
          also includes the filter bar card above this one. */}
        <div
          className={css({
            position: 'relative',
            flex: '1',
            minHeight: `${INBOX_PANES_MIN_HEIGHT_PX}px`,
            display: 'grid',
            gridTemplateRows: 'minmax(0, 1fr)',
            gridTemplateColumns: isDesktop
              ? `${LIST_PANE_WIDTH_PX}px minmax(0, 1fr) ${RAIL_WIDTH_PX}px`
              : `${LIST_PANE_WIDTH_PX}px minmax(0, 1fr)`,
            backgroundColor: palette.white,
            border: `${INBOX_BORDER_WIDTH_PX}px solid ${palette.grey20}`,
            borderRadius: `${INBOX_CARD_BORDER_RADIUS_PX}px`,
            boxShadow: INBOX_CARD_SHADOW,
            overflow: 'hidden',
          })}
        >
          <section
            aria-label={t('list_landmark')}
            className={`${paneStyle} ${listPaneStyle}`}
          >
            {findSlot(children, ListSlot)}
          </section>
          <section aria-label={t('thread_landmark')} className={paneStyle}>
            {findSlot(children, ThreadSlot)}
          </section>
          {isDesktop && (
            <section
              aria-label={t('rail_landmark')}
              className={`${paneStyle} ${railPaneStyle}`}
            >
              {findSlot(children, RailSlot)}
            </section>
          )}
          {!isDesktop && railOpen && (
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label={t('rail_drawer_aria')}
              tabIndex={-1}
              className={css({
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: `${RAIL_WIDTH_PX}px`,
                overflow: 'auto',
                backgroundColor: palette.white,
                borderLeft: `${INBOX_BORDER_WIDTH_PX}px solid ${palette.grey30}`,
                boxShadow: RAIL_DRAWER_SHADOW,
              })}
            >
              {findSlot(children, RailSlot)}
            </div>
          )}
        </div>
      </div>
    </InboxRailContext.Provider>
  )
}

InboxLayout.FilterBar = FilterBarSlot
InboxLayout.List = ListSlot
InboxLayout.Thread = ThreadSlot
InboxLayout.Rail = RailSlot

export default InboxLayout
